// Copyright (c) Bentley Systems
import { ElistDataModel } from "../DataModels/elist-data-model";
import { ElistStructureModel } from "../DataModels/elist-structure-model";
import { ActionVm } from "../../Action/action-vm";
import { ActionParameterVm } from "../../Action/action-parameter-vm";
import { CellVm } from "./cell-vm";
import { Style } from "../../DataModels/style";
import {
  getCssValueFromString,
  getQueryParam,
  isEmptyObject,
  meetCondition,
} from "../../Helpers/helper-functions";
import { LegendHighlight } from "../Legend/legend-highlight";
import { Parameter } from "../../Action/parameter";
import { VariantVm } from "../Variant/variant-vm";
import { ConfirmationText } from "../../Action/confirmation-text";
import { Column } from "./column";
import { ToastNotification } from "../../Action/toast-notification";

export class ElistViewModel {
  private _dataModel: ElistDataModel;
  private _urlParameters: Parameter[];

  public structureModel: ElistStructureModel;
  public header: string | undefined;
  public headerActions: ActionVm[] = [];
  public headerBackAction: ActionVm | undefined;
  public variants: VariantVm[] = [];
  public pageSize: number | undefined;
  public columns: Column[] = [];
  public values: CellVm[][] = [];
  public isLegendVisible: boolean;
  public legendHighlights: LegendHighlight[] = [];
  public isTile: boolean = false;
  public isBackToEntryScreenQueryParameter = getQueryParam("backToEntryScreen");

  constructor(
    structureModel: ElistStructureModel,
    dataModel: ElistDataModel,
    urlParameters: Parameter[],
    currentVariantId: number | undefined,
  ) {
    this.structureModel = structureModel;
    this.isTile = structureModel.isTile;
    this._dataModel = dataModel;
    this._urlParameters = urlParameters;
    this.pageSize =
      structureModel.elLayouts.length > 0 &&
      structureModel.elLayouts[0].pageSize !== undefined
        ? structureModel.elLayouts[0].pageSize
        : 25;
    this.header = structureModel.captionText;

    this.populateVariants(currentVariantId);
    this.setHeaderActions();

    this.setViewModelColumnsAndValues();
    this.isLegendVisible =
      this.structureModel.elHighlights.find(
        (h) =>
          h.elHighlightTexts.length > 0 &&
          h.elHighlightTexts[0].description !== null &&
          h.elHighlightTexts[0].description !== "",
      ) !== undefined;
    if (this.isLegendVisible === true) {
      this.structureModel.elHighlights.forEach((h) => {
        if (
          h.elHighlightTexts.length > 0 &&
          h.elHighlightTexts[0].description !== null &&
          h.elHighlightTexts[0].description !== ""
        ) {
          const legendHighlight = new LegendHighlight();
          legendHighlight.highlightId = h.highlightId;
          legendHighlight.caption = h.elHighlightTexts[0].caption;
          legendHighlight.description = h.elHighlightTexts[0].description;
          legendHighlight.styleId = h.styleId;
          const style = this.structureModel.otherStyles.find(
            (s) => s.styleId === h.styleId,
          );
          if (style !== undefined) {
            legendHighlight.styleData = style.data;
          }
          this.legendHighlights.push(legendHighlight);
        }
      });
    }
  }

  private populateVariants(currentVariantId: number | undefined) {
    this.structureModel.elVariants.forEach((variant) => {
      const label =
        variant.elVariantTexts.length > 0 &&
        variant.elVariantTexts[0].text !== undefined &&
        variant.elVariantTexts[0].text !== null &&
        variant.elVariantTexts[0].text !== ""
          ? variant.elVariantTexts[0].text
          : variant.name;
      this.variants.push(
        new VariantVm(
          variant.variantId as number,
          variant.variantOrder as number,
          label as string,
          variant.name as string,
          variant.whereClause,
        ),
      );
    });

    let varriantId: number | undefined;
    if (currentVariantId !== undefined) {
      varriantId = currentVariantId;
    } else if (this.structureModel.defaultVariant !== undefined) {
      varriantId = this.structureModel.defaultVariant.variantId;
    }
    if (varriantId !== undefined) {
      const defVariant = this.variants.find((v) => v.id === varriantId);
      if (defVariant !== undefined) {
        defVariant.isActive = true;
      }
    }
  }

  private getActiveVariant() {
    const activeVariant = this.variants.find((v) => v.isActive);
    if (activeVariant !== null) {
      return activeVariant;
    }
    return undefined;
  }

  private getLayout() {
    if (this.structureModel.elLayouts.length > 0) {
      if (this.structureModel.elConditionRanges.length > 0) {
        const activeVariant = this.getActiveVariant();
        if (activeVariant !== undefined) {
          for (const cr of this.structureModel.elConditionRanges) {
            // compare type: Executor and compare value: name of variant
            if (cr.type === "E" && cr.value === "Variant.Name") {
              // from type == toType == constant
              // from value == min value
              // means eg VariantName = "value"
              if (
                cr.maxType === "C" &&
                cr.minType === "C" &&
                cr.maxValue === cr.minValue
              ) {
                if (cr.minValue === activeVariant.name) {
                  for (const l of this.structureModel.elLayouts) {
                    const condition = l.elLayoutConditions.find(
                      (c) => c.conditionId === cr.conditionId,
                    );
                    if (condition !== undefined) {
                      return l;
                    }
                  }
                }
              }
            }
          }
        }
      } else {
        // return first based on order
        return this.structureModel.elLayouts.reduce((aLayout, bLayout) =>
          (aLayout.layoutOrder as number) < (bLayout.layoutOrder as number)
            ? aLayout
            : bLayout,
        );
      }
    }

    return undefined;
  }

  private setHeaderActions() {
    const headerActions: ActionVm[] = [];
    const layout = this.getLayout();
    if (layout !== undefined) {
      layout.elLayoutActions
        .filter((la) => la.type === "H" && la.controlOrder !== undefined)
        .sort((a, b) => (a.controlOrder as number) - (b.controlOrder as number))
        .forEach((la) => {
          const actionDo = this.structureModel.elActions.find(
            (a: { actionId: number }) => a.actionId === la.actionId,
          );
          const action = new ActionVm();
          action.caption =
            actionDo.elActionTexts !== undefined &&
            actionDo.elActionTexts.length > 0
              ? actionDo.elActionTexts[0].text
              : "";
          action.actionId = actionDo.actionId;
          action.hasConfirmation = actionDo.confirmationType === "Y";
          if (action.hasConfirmation && actionDo.confirmationText != null) {
            action.confirmationText = new ConfirmationText(
              actionDo.confirmationText,
            );
          }
          if (actionDo.toastNotification) {
            action.toastNotification = new ToastNotification(
              actionDo.toastNotification,
            );
          }
          action.targetNodeId = actionDo.targetedNode?.nodeId;
          action.targetNodeType = actionDo.targetedNode?.nodeType;
          action.targetDawComponent = actionDo.targetDawComponent;
          action.f_ROLL = actionDo.f_ROLL;
          action.isBackHeaderButton = actionDo.f_BACK;
          action.isBackToEntryScreen = actionDo.backToEntryScreen;

          if (actionDo.styleId !== undefined) {
            const styleVm = new Style();
            styleVm.styleId = actionDo.styleId;
            action.style = styleVm;
            const style = this.structureModel.otherStyles.find(
              (s) => s.styleId === action.style?.styleId,
            );
            if (style !== undefined) {
              action.style.data = style.data;
              action.style.backgroundImageBase64Str =
                style.backgroundImageBase64Str;
              action.style.customData = style.customData;
            }
          }

          const actionParams: any[] = actionDo.elActionParameters;
          actionParams.forEach((p) => {
            const par = new ActionParameterVm();
            par.actionParamId = p.actionParamId;
            par.name = p.name;
            par.value = p.value;
            par.valueType = p.valueType;
            if (par.valueType === "P") {
              const urlPar = this._urlParameters.find(
                (fp) => fp.name === par.name,
              );
              if (urlPar !== undefined) {
                par.value = urlPar.value;
              }
            }
            action.actionParameters.push(par);
          });
          if (action.isBackHeaderButton) {
            if (action.isBackToEntryScreen === true) {
              // do not show if it's meant only for back to entry screen and no targetNode or TargetComponent is set
              if (
                action.targetDawComponent ||
                action.targetNodeId ||
                this.isBackToEntryScreenQueryParameter === "true"
              ) {
                this.headerBackAction = action;
              }
            } else {
              this.headerBackAction = action;
            }
          } else {
            headerActions.push(action);
          }
        });
    }
    this.headerActions = headerActions;
  }

  private setViewModelColumnsAndValues() {
    const columnVms: Column[] = [];
    const valuesVms: any[][] = [];
    for (let i = 0; i < this._dataModel.values.length; i++) {
      const row: any[] = [];
      for (let j = 0; j < this._dataModel.values[i].length; j++) {
        const cellVm = new CellVm();

        if (typeof this._dataModel.values[i][j] == "boolean") {
          cellVm.value = this._dataModel.values[i][j];
        } else if (
          !Number.isFinite(this._dataModel.values[i][j]) &&
          isEmptyObject(this._dataModel.values[i][j])
        ) {
          cellVm.value = "";
        } else {
          cellVm.value = this._dataModel.values[i][j];
        }
        row[j] = cellVm;
      }
      valuesVms[i] = row;
    }
    const currentLayout = this.getLayout();
    if (currentLayout !== undefined) {
      this._dataModel.columns.forEach((dmColumn) => {
        const field = this.structureModel.elFields.find(
          (f) => f.fieldName === dmColumn.name,
        );
        if (field !== undefined) {
          const fieldCaption =
            field.elFieldTexts !== undefined && field.elFieldTexts.length > 0
              ? field.elFieldTexts[0].text
              : undefined;

          const sortedField = currentLayout.elLayoutFields.find(
            (f) => f.fieldId === (field).fieldId,
          );
          const controlOrder =
            sortedField !== undefined
              ? sortedField.controlOrder
              : this._dataModel.columns.length - 1;
          const width =
            sortedField !== undefined ? sortedField.width : undefined;
          const isColor = (field).dawPicker === true;
          const isAttachment = (field).isAttachment === true;
          let isTileName = false;
          let isImgBase64 = false;
          if (this.isTile) {
            isTileName = (field).isTileName === true;
            isImgBase64 = (field).isTileImagePath === true;
          } else {
            isImgBase64 = (field).isImgBase64Str === true;
          }
          const columnVm = new Column(
            dmColumn,
            controlOrder,
            field.fieldId,
            fieldCaption,
            width,
            isColor,
            isTileName,
            isImgBase64,
            isAttachment,
            (field).disableToggleVisibility,
          );
          columnVms.push(columnVm);
          const pushedIndex = columnVms.length - 1;
          const columnIndex = this._dataModel.columns.indexOf(dmColumn);
          if (pushedIndex < columnIndex) {
            valuesVms.forEach((v) => {
              this.swapElements(v, pushedIndex, columnIndex);
            });
          }
        }
      });

      const sortedFields = currentLayout.elLayoutFields.sort(
        (a, b) => (a.controlOrder as number) - (b.controlOrder as number),
      );

      for (let i = 0; i < sortedFields.length; i++) {
        const columnVm = columnVms.find(
          (c) => c.fieldId === sortedFields[i].fieldId,
        );
        if (columnVm !== undefined) {
          const columnVmIndex = columnVms.indexOf(columnVm);
          if (columnVmIndex !== -1) {
            this.swapElements(columnVms, columnVmIndex, i);
            valuesVms.forEach((v) => {
              this.swapElements(v, columnVmIndex, i);
            });
          }
        }
      }

      if (sortedFields.length < columnVms.length) {
        columnVms.forEach((cvm) => {
          const sortedField = sortedFields.find(
            (sf) => sf.fieldId === cvm.fieldId,
          );
          if (sortedField === undefined) {
            cvm.isHidden = true;
          }
        });
      }
    }

    this.columns = columnVms;
    this.values = valuesVms;

    this.addItemActionColumns();
    this.applyHighlights();
    this.applyItemActionConditions();
  }

  private swapElements = (array: any[], index1: number, index2: number) => {
    [array[index1], array[index2]] = [array[index2], array[index1]];
  };

  private applyHighlights() {
    if (this.structureModel.elHighlights.length > 0) {
      this.structureModel.elHighlights
        // apply to whole row
        .filter(
          (hforRow) =>
            hforRow.applyToFieldId === null && hforRow.applyToActionId === null,
        )
        .forEach((h) => {
          if (h.fromValue === undefined && h.toValue === undefined) {
            return;
          }
          const style = this.structureModel.otherStyles.find(
            (s) => s.styleId === h.styleId,
          );
          if (style === undefined) {
            return;
          }
          const columnIndex = this.columns.findIndex(
            (c) => c.name === h.fieldName,
          );
          if (columnIndex !== -1) {
            this.values.forEach((v) => {
              v.forEach((cellValue) => {
                if (
                  meetCondition(h.fromValue, h.toValue, v[columnIndex].value)
                ) {
                  cellValue.style = (style).data;
                }
              });
            });
          }
        });
      this.structureModel.elHighlights
        // apply only for defined column by FieldId
        .filter(
          (hforRow) =>
            hforRow.applyToFieldId !== null || hforRow.applyToActionId !== null,
        )
        .forEach((h) => {
          if (h.fromValue === undefined && h.toValue === undefined) {
            return;
          }
          const style = this.structureModel.otherStyles.find(
            (s) => s.styleId === h.styleId,
          );
          if (style === undefined) {
            return;
          }
          const columnIndex = this.columns.findIndex(
            (c) => c.name === h.fieldName,
          );
          if (columnIndex !== -1) {
            this.values.forEach((v) => {
              if (meetCondition(h.fromValue, h.toValue, v[columnIndex].value)) {
                const applyStyleColumnIndex = this.columns.findIndex(
                  (c) =>
                    c.dataType !== "ItemAction" &&
                    c.fieldId === h.applyToFieldId,
                );
                if (applyStyleColumnIndex !== -1) {
                  v[applyStyleColumnIndex].style = (style).data;
                }

                const applyActionStyleColumnIndex = this.columns.findIndex(
                  (c) =>
                    c.dataType === "ItemAction" &&
                    c.fieldId === h.applyToActionId,
                );
                if (
                  applyActionStyleColumnIndex !== -1 &&
                  v[applyActionStyleColumnIndex].action !== undefined
                ) {
                  const action = v[applyActionStyleColumnIndex]
                    .action;
                  if (action.style === undefined) {
                    action.style = new Style();
                  }
                  action.style.data = (style).data;
                  action.style.customData = (style).customData;
                }
              }
            });
          }
        });
    }
  }

  private applyItemActionConditions() {
    const columnIndexesWithVisibilityConditionOnItemAction: number[] = [];
    this.columns.forEach((column, index) => {
      if (column.isItemActionVisibilityCondition === true) {
        columnIndexesWithVisibilityConditionOnItemAction.push(index);
      }
    });

    columnIndexesWithVisibilityConditionOnItemAction.forEach((i) => {
      this.values.forEach((v) => {
        const action = v[i].action as ActionVm;
        const conditionFieldColIndex = this.columns.findIndex(
          (c) => c.name === action.visCompareValue,
        );
        if (conditionFieldColIndex !== -1 &&
          !meetCondition(
            action.visFromValue,
            action.visToValue,
            v[conditionFieldColIndex].value,
          )
        ) {
          action.isVisible = false;
        }
      });
    });
  }

  private addItemActionColumns() {
    const itemActions = this.getItemActions();
    if (itemActions.length > 0) {
      itemActions.forEach((action) => {
        const itemActionColumn = new Column();

        itemActionColumn.name = action.caption;
        itemActionColumn.dataType = "ItemAction";
        if (action.isVisibilityCondition === true) {
          itemActionColumn.isItemActionVisibilityCondition = true;
        }
        itemActionColumn.isActionInThreeDotMenu = action.isInThreeDotMenu;
        itemActionColumn.fieldId = action.actionId;
        itemActionColumn.controlOrder = action.controlOrder;

        if (action.style !== undefined && action.style.data !== undefined) {
          const widthStr = getCssValueFromString(action.style.data, "width");
          if (widthStr !== "") {
            itemActionColumn.width = parseInt(widthStr, 10);
          }
        }
        const insertIndex = this.getInsertIndex(itemActionColumn.controlOrder);
        if (insertIndex !== undefined) {
          this.values.forEach((values) => {
            const newCopy = JSON.parse(JSON.stringify(action)) as ActionVm;
            // JSON replace function with null and function in object with undefined - I need function
            // https://www.samanthaming.com/tidbits/70-3-ways-to-clone-objects/
            const newCellActionVm = new ActionVm(newCopy);
            if (newCellActionVm.actionParameters.length > 0) {
              newCellActionVm.actionParameters.forEach((par) => {
                const columnIndex = this.columns.findIndex(
                  (c) => c.name === par.value,
                );
                if (par.valueType === "F") {
                  par.value = values[columnIndex].value;
                }
                if (par.valueType === "P") {
                  const urlPar = this._urlParameters.find(
                    (p) => p.name === par.name,
                  );
                  if (urlPar != null) {
                    par.value = urlPar.value;
                  }
                }
              });
            }
            const cellWithAction = new CellVm();
            cellWithAction.action = newCellActionVm;
            values.splice(insertIndex, 0, cellWithAction);
          });
          this.columns.splice(insertIndex, 0, itemActionColumn);
        }
      });
    }
  }

  private getInsertIndex(controlOrder: number | undefined) {
    let insertIndex: number | undefined;
    if (this.columns.length > 0) {
      const visibleColumns = this.columns.filter((c) => c.isHidden === false);
      if (visibleColumns.length === 0) {
        insertIndex = 0;
      } else {
        const lastIndex = visibleColumns.length - 1;
        if (controlOrder !== undefined) {
          if (
            visibleColumns[0].controlOrder !== undefined &&
            controlOrder < visibleColumns[0].controlOrder
          ) {
            insertIndex = 0;
          } else if (
            visibleColumns[lastIndex].controlOrder !== undefined &&
            controlOrder >= (visibleColumns[lastIndex].controlOrder)
          ) {
            insertIndex = lastIndex + 1;
          } else {
            for (let i = 0; i < visibleColumns.length; i++) {
              if (visibleColumns[i].controlOrder !== undefined) {
                if ((visibleColumns[i].controlOrder as number) > controlOrder) {
                  insertIndex = i;
                  break;
                }
              }
            }
          }
        }
      }
    }
    return insertIndex;
  }

  private getItemActions = (): ActionVm[] => {
    const itemActions: ActionVm[] = [];
    const actions = this.structureModel.elActions.filter(
      (a: { actionType: string }) => a.actionType === "I",
    );
    const currentLayout = this.getLayout();
    if (currentLayout !== undefined) {
      actions.forEach((a) => {
        const layoutAction = (currentLayout).elLayoutActions.find(
          (la) => la.actionId === a.actionId,
        );
        if (layoutAction !== undefined) {
          const action = new ActionVm();
          action.isItemAction = true;
          action.isInThreeDotMenu = a.isInThreeDotMenu;
          action.hasConfirmation =
            a.confirmationType !== undefined && a.confirmationType === "Y";
          if (action.hasConfirmation) {
            action.confirmationText = new ConfirmationText(a.confirmationText);
          }
          if (a.toastNotification) {
            action.toastNotification = new ToastNotification(
              a.toastNotification,
            );
          }

          action.actionId = a.actionId;
          action.caption = a.elActionTexts[0].text;
          action.controlOrder = layoutAction.controlOrder;
          action.targetNodeId = a.targetedNode?.nodeId;
          action.targetNodeType = a.targetedNode?.nodeType;
          action.targetDawComponent = a.targetDawComponent;
          action.visFromValue = a.visFromValue;
          action.visFromType = a.visFromType;
          action.visToValue = a.visToValue;
          action.visToType = a.visToType;
          action.visCompareValue = a.visCompareValue;
          action.visCompareType = a.visCompareType;
          action.f_ROLL = a.f_ROLL;
          if (a.styleId !== undefined) {
            const style = this.structureModel.otherStyles.find(
              (s) => s.styleId === a.styleId,
            );
            if (style !== undefined) {
              action.style = style;
            }
          }

          const params: any[] = a.elActionParameters;
          if (params !== undefined && params.length > 0) {
            params.forEach((param) => {
              const par = new ActionParameterVm();
              par.actionParamId = param.actionParamId;
              par.name = param.name;
              par.value = param.value;
              par.valueType = param.valueType;
              action.actionParameters.push(par);
            });
          }

          itemActions.push(action);
        }
      });
    }
    return itemActions.sort(
      (a, b) => (a.controlOrder as number) - (b.controlOrder as number),
    );
  };
}
